import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { updateLocationYup } from 'utils/validate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { updateFloorPlan, getFloorPlan, getLocations } from 'services/locations';
import { RoomInfo } from 'types/room';
import { isVerticalLocationLevel } from 'utils/renderTitleLocation';
import * as pdfjsLib from 'pdfjs-dist';
import imageCompression from 'browser-image-compression';
import { SIZE_LOCATIONS_PAGE } from 'const/paging';
import { useFlash } from 'contexts/flash';

type FormUpdateFloorPlan = {
  value: string;
  floorPlan?: File | null;
};

type compressionoptions = {
  maxSizeMB: number;
  maxWidthOrHeight: number;
};

export const useInjection = () => {
  const navigate = useNavigate();
  const { clientId, locationId, clientName } = useParams();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [floorplanurl, setfloorplanurl] = useState<string>('');
  const [floorplandata, setfloorplandata] = useState<number>();
  const [floorplanfile, setfloorplanfile] = useState<File>();
  const { setFlash } = useFlash();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<FormUpdateFloorPlan>({ resolver: yupResolver(updateLocationYup) });

  const {
    fields: fieldNames,
    append,
    remove
  } = useFieldArray({
    control,
    name: 'names' as never
  });

  const [roomInfo, setRoomInfo] = useState<RoomInfo>([]);

  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
  ).toString();

  const paths: RoomInfo = useMemo(() => {
    return [
      { name: 'PDF Mapping / Upload', level: 999, onClick: () => navigate('/pdfmapping') },
      {
        name: clientName || '',
        id: Number(clientId),
        isClient: true,
        level: 998,
        onClick: () => navigate(`/pdfmapping/uploadplan/${clientName}/${clientId}/location`)
      },
      ...roomInfo
        .sort((a, b) => a.level - b.level)
        .map((item) => {
          const href = isVerticalLocationLevel(item.level)
            ? `/pdfmapping/uploadplan/${clientName}/${clientId}/location/${item.id}`
            : `/pdfmapping/uploadplan/${clientName}/${clientId}/location/${item.id}`;
          return {
            ...item,
            onClick: () => navigate(href)
          };
        })
    ];
  }, [clientId, clientName, navigate, roomInfo]);
  const initialize = useCallback(async (): Promise<void> => {
    setLoading(true);

    try {
      const params = {
        size: SIZE_LOCATIONS_PAGE,
        page: 1,
        parentId: locationId ? locationId : undefined,
        clientId: clientId ? clientId : undefined
      };
      const [locationsResult, floorPlanResult] = await Promise.allSettled([
        getLocations(params) as Promise<{ room: RoomInfo }>,
        getFloorPlan({ id: Number(locationId) })
      ]);

      if (locationsResult.status !== 'fulfilled') {
        throw locationsResult.reason;
      }

      const locationsResponse = locationsResult.value;

      setRoomInfo(locationsResponse.room);

      if (floorPlanResult.status === 'fulfilled') {
        if (floorPlanResult.value.data.floorPlan !== null) {
          setfloorplandata(floorPlanResult.value.data.floorPlan[0].id);
          reset({
            value: floorPlanResult.value.data.floorPlan[0].name ?? ''
          });
          setfloorplanurl(floorPlanResult.value.data.floorPlan[0].url);
          const datafile = await urlToFile(floorPlanResult.value.data.floorPlan[0].url);
          setfloorplanfile(datafile);
        }
      } else {
        setfloorplanurl('');
      }
    } catch (error) {
      console.error('Failed to initialize floor plan.', error);
    } finally {
      setLoading(false);
    }
  }, [clientId, locationId, reset]);

  const { asyncCallback: asyncInitialize } = useAsyncCallback(initialize, []);

  const urlToFile = async (url: string): Promise<File> => {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }

    const blob = await response.blob();

    const extension = 'png';

    return new File([blob], `floorplan.${extension}`, {
      type: blob.type,
      lastModified: Date.now()
    });
  };

  useEffect(() => {
    asyncInitialize();
  }, [asyncInitialize]);

  const handleUpdateFloorPlan = useCallback(
    async (value: FormUpdateFloorPlan) => {
      if (!floorplandata) return;

      const fileToUpload = value.floorPlan ?? floorplanfile;

      if (!fileToUpload) return;

      await updateFloorPlan({
        locationid: Number(locationId),
        floorplanid: floorplandata,
        file: fileToUpload,
        name: value.value
      });

      setFlash({ type: 'success', message: 'Floor Plan Updated Successfully' });

      const newPath = location.pathname.replace(`/update`, `/plans`);
      navigate(newPath);
    },
    [floorplandata, locationId, floorplanfile]
  );

  const { asyncCallback: asyncupdateFloorPlan } = useAsyncCallback(handleUpdateFloorPlan, [
    handleUpdateFloorPlan
  ]);

  const uploadfloorplan = async (file: File) => {
    if (!file) return;
    if (file?.type === 'image/png') {
      const compressimg = await imagecompress(file);
      return compressimg;
    } else if (file?.type === 'application/pdf') {
      const imgfile = await convertPdfToImage(file);
      return imgfile;
    }
  };

  const convertPdfToImage = async (file: File) => {
    if (!file) return;
    try {
      const pdfBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(pdfBuffer) }).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 3 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Canvas is not supported');
      }
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: context, viewport, canvas }).promise;
      const image: File = await new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            throw new Error('Canvas conversion to blob failed');
          }
          const fileName = file.name.substring(0, file.name.lastIndexOf('.')) + '.png';
          const fileObj = new File([blob], fileName, { type: 'image/png' });
          resolve(fileObj);
        }, 'image/png');
      });
      const img = await imagecompress(image);
      return img;
    } catch (error) {
      console.error(error);
    }
  };

  const imagecompress = async (image: File) => {
    const options: compressionoptions = {
      maxSizeMB: Number(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 1,
      maxWidthOrHeight: Number(process.env.REACT_APP_MAX_WIDTHORHEIGHT_PX) || 1920
    };
    const img = await imageCompression(image, options);
    return new File([img], image.name, {
      type: img.type || image.type,
      lastModified: Date.now()
    });
  };

  return {
    backToClient: () => navigate(`${location.pathname.replace('/update', '')}`),
    handleSubmit,
    errors,
    uploadfloorplan,
    fieldNames,
    append,
    remove,
    control,
    paths,
    isLoading,
    floorplanurl,
    asyncupdateFloorPlan,
    floorplanfile
  };
};
